import useFetch from "hooks/useFetch";
import React from "react";
import api from "utils/api";
import { formatEmployeeGroup } from "utils/utils";
import useAuth from "hooks/useAuth";

const EmployeeDirectoryCtx = React.createContext(null);
const EmployeeDirectoryApiCtx = React.createContext(null);

const initialState = {
  count: 10,
  page: 1,
  search: "",
  groups: [],
  withoutRole: null,
  excludedEmployees: [],
  checkedEmployees: [],
  hasEmail: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "CURRENT_PAGE":
      return { ...state, page: action.payload };
    case "SEARCH":
      return { ...state, search: action.payload, page: initialState.page };
    case "GROUPS":
      return { ...state, groups: action.payload, page: initialState.page };
    case "WITHOUT_ROLE":
      return { ...state, withoutRole: action.payload, page: initialState.page };
    case "CHECK_EMPLOYEE":
      return {
        ...state,
        checkedEmployees: [...state.checkedEmployees, action.payload],
      };
    case "UNCHECK_EMPLOYEE":
      return {
        ...state,
        checkedEmployees: state.checkedEmployees.filter(
          (employee) => employee.id !== action.payload.id
        ),
      };
    case "CHECK_SINGLE_EMPLOYEE":
      return {
        ...state,
        checkedEmployees: [action.payload],
      };
    case "UNCHECK_SINGLE_EMPLOYEE":
      return {
        ...state,
        checkedEmployees: [],
      };
    case "CLEAR":
      return initialState;
    default:
      return state;
  }
};

// custom provider
export const EmployeeDirectoryProvider = ({ children, values = {} }) => {
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    ...values,
  });
  const { user } = useAuth();

  const actions = React.useMemo(
    () => ({
      setPage: (payload) => dispatch({ type: "CURRENT_PAGE", payload }),
      setSearch: (payload) => dispatch({ type: "SEARCH", payload }),
      setGroups: (payload) => dispatch({ type: "GROUPS", payload }),
      setWithoutRole: (payload) => dispatch({ type: "WITHOUT_ROLE", payload }),
      setCheckEmployee: (payload) =>
        dispatch({ type: "CHECK_EMPLOYEE", payload }),
      setUncheckEmployee: (payload) =>
        dispatch({ type: "UNCHECK_EMPLOYEE", payload }),
      setCheckSingleEmployee: (payload) =>
        dispatch({ type: "CHECK_SINGLE_EMPLOYEE", payload }),
      setUncheckSingleEmployee: (payload) =>
        dispatch({ type: "UNCHECK_SINGLE_EMPLOYEE", payload }),
      clear: () => dispatch({ type: "CLEAR" }),
    }),
    [dispatch]
  );

  const query = useFetch(
    React.useCallback(
      () =>
        api.getEmployeeDirectory({
          count: state.count,
          page: state.page,
          search: state.search,
          groups: state.groups,
          withoutRole: state.withoutRole,
          excludedEmployees: state.excludedEmployees,
          showInstructors: state.showInstructors,
          hasEmail: state.hasEmail,
        }),
      [
        state.count,
        state.page,
        state.search,
        state.groups,
        state.withoutRole,
        state.excludedEmployees,
        state.showInstructors,
        state.hasEmail,
      ]
    )
  );

  const data = {
    ...query.data,
    rows: query.data?.rows?.map((element) => ({
      ...element,
      name: `${element.firstName} ${element.lastName}`,
      job: element?.job_title?.job,
      role: element?.role?.roles_trans
        ? element?.role?.roles_trans[0]?.name
        : "",
      entity: formatEmployeeGroup(element?.company_group, user.rootGroupId),
      employee_id: element?.employee_id,
    })),
  };

  return (
    <EmployeeDirectoryCtx.Provider value={{ ...state, ...actions }}>
      <EmployeeDirectoryApiCtx.Provider value={{ ...query, data }}>
        {children}
      </EmployeeDirectoryApiCtx.Provider>
    </EmployeeDirectoryCtx.Provider>
  );
};

// custom hook
export const useEmployeeDirectoryState = () => {
  const ctx = React.useContext(EmployeeDirectoryCtx);

  if (!ctx) {
    throw new Error(
      "useEmployeeDirectoryState must be used within the EmployeeDirectoryProvider"
    );
  }

  return ctx;
};

// custom hook
export const useEmployeeDirectoryApi = () => {
  const ctx = React.useContext(EmployeeDirectoryApiCtx);

  if (!ctx) {
    throw new Error(
      "useEmployeeDirectoryApi must be used within the EmployeeDirectoryProvider"
    );
  }

  return ctx;
};
